import React, { useState } from 'react';
import { changeMyPassword, changeMyUsername, sendPushTest } from '../services/api';
import { showSuccess, showError, showInfo } from './ToastNotifications';
import { getPushState, enablePush } from '../utils/push';
import { passwordPolicyError, PASSWORD_RULE_TEXT } from '../utils/passwordPolicy';
import PasswordChecklist from './PasswordChecklist';
import TwoFactorEnrol from './TwoFactorEnrol';
import PasskeyList from './PasskeyList';
import { ROLE_LABELS } from './RoleSwitcher';
import { formatDate } from '../utils/helpers';
import './ConfirmDeleteModal.css';

// "My account" — the signed-in person's own password and sign-in name
// (operator 2026-10-08: staff had no way to change either). Both actions
// prove possession with the current password; the server enforces the same
// 12-character minimum as user creation. A sign-in name change reloads the app
// so every page picks up the new name. Since 2026-10-30 (user identities) these
// belong to the PERSON: one password, authenticator and passkey set serve every
// role; the name shown on records (nickname) is not changed here.
export default function AccountModal({ isOpen, onClose, user }) {
  const [curPw, setCurPw] = useState('');
  const [newPw, setNewPw] = useState('');
  const [newPw2, setNewPw2] = useState('');
  const [busyPw, setBusyPw] = useState(false);
  const [newName, setNewName] = useState('');
  const [curPwName, setCurPwName] = useState('');
  const [busyName, setBusyName] = useState(false);
  const [pushState, setPushState] = useState(() => getPushState());
  const [busyPush, setBusyPush] = useState(false);
  // Two-factor (every account): 'enrol' = inline setup again (new phone).
  const [tfMode, setTfMode] = useState(null);
  if (!isOpen) return null;
  const loginName = user?.identity?.login_name || user?.username;
  const roles = Array.isArray(user?.roles) ? user.roles : [];

  // Device alerts (2026-10-09): one tap proves the whole push path for THIS
  // account — the test goes through the ordinary notification trigger to every
  // device the account registered, so "it never arrived" can be told apart
  // from "this device was never registered".
  const onEnableAlerts = async () => {
    setBusyPush(true);
    const r = await enablePush();
    setBusyPush(false);
    setPushState(getPushState());
    if (r === 'granted') showSuccess('Alerts turned on for this device');
    else if (r === 'denied') showError('Notifications are blocked for this site — allow them in the browser\'s site settings, then reload');
    else if (r === 'unsupported') showError('This browser cannot receive alerts here (iPhone: add the CRM to the Home Screen first)');
  };
  const onTestAlert = async () => {
    setBusyPush(true);
    try {
      const r = await sendPushTest();
      if (r?.devices > 0) showInfo(`Test alert sent to ${r.devices} registered device${r.devices === 1 ? '' : 's'} — it should appear within seconds.`);
      else showError('This account has no registered device yet. Turn on alerts on the device you want them on, then try again.');
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to send a test alert');
    } finally { setBusyPush(false); }
  };
  const pushStatusText = !pushState.supported
    ? 'This browser cannot receive alerts (on iPhone, add the CRM to the Home Screen and open it from there).'
    : pushState.permission === 'granted' ? 'This device is allowed to show alerts.'
      : pushState.permission === 'denied' ? 'Notifications are blocked for this site on this device.'
        : 'Alerts are not turned on for this device yet.';

  const submitPassword = async (e) => {
    e.preventDefault();
    const policyError = passwordPolicyError(newPw, { username: loginName });
    if (policyError) return showError(policyError);
    if (newPw !== newPw2) return showError('The two new passwords do not match');
    setBusyPw(true);
    try {
      const r = await changeMyPassword(curPw, newPw);
      showSuccess(r?.message || 'Password changed');
      setCurPw(''); setNewPw(''); setNewPw2('');
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to change password');
    } finally { setBusyPw(false); }
  };

  const submitUsername = async (e) => {
    e.preventDefault();
    setBusyName(true);
    try {
      const r = await changeMyUsername(newName.trim(), curPwName);
      showSuccess(`Sign-in name changed to ${r?.login_name || r?.username || newName.trim()} — reloading…`);
      setTimeout(() => window.location.reload(), 900);
    } catch (err) {
      showError(err.response?.data?.error || 'Failed to change sign-in name');
      setBusyName(false);
    }
  };

  const closeTf = () => setTfMode(null);

  const field = { width: '100%', marginBottom: 8 };
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>My account — {loginName}</h3>
          <button type="button" onClick={onClose} className="close-button" disabled={busyPw || busyName}>&times;</button>
        </div>
        <div className="modal-body" style={{ textAlign: 'left' }}>
          {roles.length > 1 && (
            <small className="form-text" style={{ display: 'block', marginBottom: 12 }}>
              Your roles: {roles.map((r) => `${r.nickname} (${ROLE_LABELS[r.role] || r.role})`).join(' · ')}. Switch with your name at the top.
            </small>
          )}
          <form onSubmit={submitPassword} style={{ marginBottom: 20 }}>
            <h4 style={{ margin: '0 0 8px' }}>Change password</h4>
            <input className="form-control" style={field} type="password" placeholder="Current password" autoComplete="current-password" value={curPw} onChange={(e) => setCurPw(e.target.value)} required />
            <input className="form-control" style={field} type="password" placeholder="New password" autoComplete="new-password" value={newPw} onChange={(e) => setNewPw(e.target.value)} required minLength={12} />
            <small className="form-text" style={{ display: 'block', marginBottom: 4 }}>{PASSWORD_RULE_TEXT}</small>
            <PasswordChecklist password={newPw} />
            <input className="form-control" style={field} type="password" placeholder="Repeat new password" autoComplete="new-password" value={newPw2} onChange={(e) => setNewPw2(e.target.value)} required minLength={12} />
            <button type="submit" className="btn btn-primary btn-small" disabled={busyPw}>{busyPw ? 'Saving…' : 'Change password'}</button>
            <small className="form-text" style={{ display: 'block', marginTop: 6 }}>Your other signed-in devices will be signed out.</small>
          </form>
          {loginName !== 'admin' && (
            <form onSubmit={submitUsername}>
              <h4 style={{ margin: '0 0 8px' }}>Change sign-in name</h4>
              <input className="form-control" style={field} type="text" placeholder="New sign-in name (letters, digits, . _ -)" autoComplete="username" value={newName} onChange={(e) => setNewName(e.target.value)} required minLength={2} maxLength={32} pattern="[A-Za-z0-9._-]{2,32}" />
              <input className="form-control" style={field} type="password" placeholder="Current password (to confirm)" autoComplete="current-password" value={curPwName} onChange={(e) => setCurPwName(e.target.value)} required />
              <button type="submit" className="btn btn-outline btn-small" disabled={busyName}>{busyName ? 'Saving…' : 'Change sign-in name'}</button>
              <small className="form-text" style={{ display: 'block', marginTop: 6 }}>Your name on records ({user?.username}) does not change.</small>
            </form>
          )}
          <div style={{ marginTop: 20 }}>
            <h4 style={{ margin: '0 0 8px' }}>Two-factor authentication</h4>
            <small className="form-text" style={{ display: 'block', marginBottom: 8 }}>
              Status: {user?.twoFactorEnabled ? <span className="tf-status-on">On</span> : <span className="tf-status-off">Not set up</span>}
            </small>
            {tfMode === 'enrol' ? (
              <TwoFactorEnrol
                onDone={() => { closeTf(); showSuccess('Two-factor authentication set up again'); onClose(); }}
                onCancel={closeTf}
              />
            ) : (
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                <button type="button" className="btn btn-outline btn-small" onClick={() => setTfMode('enrol')}>Set up again (new phone)</button>
                <small className="form-text">Lost your phone and cannot sign in? Ask an administrator to reset your 2FA in User Management.</small>
              </div>
            )}
          </div>
          {user?.telegram && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ margin: '0 0 8px' }}>Telegram</h4>
              <small className="form-text" style={{ display: 'block' }}>
                {user.telegram.linked ? (
                  <span className="tf-status-on">
                    {user.telegram.username ? `@${user.telegram.username}` : (user.telegram.name || 'Linked')}
                    {user.telegram.linked_at ? ` · linked ${formatDate(user.telegram.linked_at)}` : ''}
                  </span>
                ) : <span className="tf-status-off">Not linked</span>}
              </small>
              <small className="form-text" style={{ display: 'block', marginTop: 4 }}>
                One Telegram account per person, shared by all your roles. Lost it? Ask an administrator to reset your Telegram in User Management.
              </small>
            </div>
          )}
          {user?.passkeyEligible && (
            <div style={{ marginTop: 20 }}>
              <h4 style={{ margin: '0 0 8px' }}>Passkeys</h4>
              <small className="form-text" style={{ display: 'block', marginBottom: 8 }}>
                Administrators sign in with a passkey or a code, and every user-management change needs a passkey. Keep one on each device you use.
              </small>
              <PasskeyList user={user} />
            </div>
          )}
          <div style={{ marginTop: 20 }}>
            <h4 style={{ margin: '0 0 8px' }}>Device alerts</h4>
            <small className="form-text" style={{ display: 'block', marginBottom: 8 }}>{pushStatusText}</small>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {pushState.supported && pushState.permission !== 'granted' && (
                <button type="button" className="btn btn-primary btn-small" onClick={onEnableAlerts} disabled={busyPush}>{busyPush ? 'Working…' : 'Turn on alerts on this device'}</button>
              )}
              <button type="button" className="btn btn-outline btn-small" onClick={onTestAlert} disabled={busyPush}>{busyPush ? 'Sending…' : 'Send test alert to my devices'}</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
